export type WorkflowLayoutPoint = { readonly x: number; readonly y: number };

export interface WorkflowLayoutNode {
  readonly id: string;
  readonly kind: 'agent' | 'human' | 'terminal';
}

export interface WorkflowLayoutEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly outcome: string;
}

export interface WorkflowLayoutGraph {
  readonly initialStage: string;
  readonly cancelledStage: string;
  readonly nodes: readonly WorkflowLayoutNode[];
  readonly edges: readonly WorkflowLayoutEdge[];
}

export interface CanonicalWorkflowLayout {
  readonly positions: Readonly<Record<string, WorkflowLayoutPoint>>;
  readonly waypoints: Readonly<Record<string, readonly WorkflowLayoutPoint[]>>;
  readonly primaryStageIds: readonly string[];
}

function primaryRoute(graph: WorkflowLayoutGraph): readonly string[] {
  const successfulTerminals = new Set(graph.nodes
    .filter((node) => node.kind === 'terminal' && node.id !== graph.cancelledStage)
    .map((node) => node.id));

  const visit = (stageId: string, route: readonly string[], seen: ReadonlySet<string>): readonly string[] => {
    if (successfulTerminals.has(stageId)) return route;
    // Exits are authored in priority order. Follow that order and backtrack
    // structurally; outcome names are deliberately user-defined.
    const candidates = graph.edges.filter((edge) => edge.from === stageId && edge.to !== graph.cancelledStage);
    for (const edge of candidates) {
      if (seen.has(edge.to)) continue;
      const found = visit(edge.to, [...route, edge.to], new Set([...seen, edge.to]));
      if (found.length > 0) return found;
    }
    return [];
  };

  const found = visit(graph.initialStage, [graph.initialStage], new Set([graph.initialStage]));
  if (found.length > 0) return found;

  // Malformed or unusual workflows still get a predictable horizontal order.
  return graph.nodes
    .filter((node) => node.id !== graph.cancelledStage)
    .map((node) => node.id);
}

export function canonicalWorkflowLayout(
  graph: WorkflowLayoutGraph,
  nodeWidth: number,
  nodeHeight: number,
): CanonicalWorkflowLayout {
  const primaryStageIds = primaryRoute(graph);
  const primaryIndex = new Map(primaryStageIds.map((id, index) => [id, index]));
  const horizontalGap = 130;
  const verticalGap = 210;
  const step = nodeWidth + horizontalGap;
  const originX = 48;
  const mainY = 80;
  const lowerY = mainY + nodeHeight + verticalGap;
  const positions: Record<string, WorkflowLayoutPoint> = {};

  primaryStageIds.forEach((id, index) => {
    positions[id] = { x: originX + index * step, y: mainY };
  });

  const humanReviewId = graph.nodes
    .filter((node) => node.kind === 'human' && !primaryIndex.has(node.id))
    .sort((left, right) => {
      const incoming = (id: string): number => graph.edges.filter((edge) => edge.to === id && primaryIndex.has(edge.from)).length;
      return incoming(right.id) - incoming(left.id);
    })[0]?.id;
  if (humanReviewId !== undefined) positions[humanReviewId] = { x: originX, y: lowerY };

  const finalPrimary = primaryStageIds.at(-1);
  if (graph.nodes.some((node) => node.id === graph.cancelledStage)) {
    positions[graph.cancelledStage] = {
      x: finalPrimary === undefined ? originX : positions[finalPrimary]!.x,
      y: lowerY,
    };
  }

  const occupiedLowerColumns = new Set(Object.values(positions)
    .filter((point) => point.y === lowerY)
    .map((point) => Math.round((point.x - originX) / step)));
  const remaining = graph.nodes.filter((node) => positions[node.id] === undefined);
  let nextLowerColumn = 1;
  remaining.forEach((node) => {
    while (occupiedLowerColumns.has(nextLowerColumn)) nextLowerColumn += 1;
    positions[node.id] = { x: originX + nextLowerColumn * step, y: lowerY };
    occupiedLowerColumns.add(nextLowerColumn);
    nextLowerColumn += 1;
  });

  const upperEdges = graph.edges.filter((edge) => {
    const from = primaryIndex.get(edge.from);
    const to = primaryIndex.get(edge.to);
    return from !== undefined && to !== undefined && to <= from;
  });
  const lowerEdges = graph.edges.filter((edge) =>
    positions[edge.from] !== undefined
    && positions[edge.to] !== undefined
    && !upperEdges.includes(edge)
    && !(primaryIndex.has(edge.from) && primaryIndex.get(edge.to) === (primaryIndex.get(edge.from) ?? -2) + 1),
  );
  const waypoints: Record<string, readonly WorkflowLayoutPoint[]> = {};

  upperEdges.forEach((edge, index) => {
    const source = positions[edge.from]!;
    const target = positions[edge.to]!;
    const laneY = mainY - 70 - index * 34;
    waypoints[edge.id] = [
      { x: source.x + nodeWidth + 28, y: laneY },
      { x: target.x - 28, y: laneY },
    ];
  });

  lowerEdges.forEach((edge, index) => {
    const source = positions[edge.from]!;
    const target = positions[edge.to]!;
    // Exception routes run below the secondary row. Keeping a full node-height
    // corridor free prevents labels and horizontal segments from disappearing
    // behind Human review or Cancelled cards.
    const laneY = lowerY + nodeHeight + 48 + index * 34;
    waypoints[edge.id] = [
      { x: source.x + nodeWidth + 28, y: laneY },
      { x: target.x - 28, y: laneY },
    ];
  });

  return { positions, waypoints, primaryStageIds };
}
